import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavBar from "../../components/NavBar/NavBar";
import { FaDownload, FaTimes, FaChevronLeft, FaChevronRight, FaFilePdf, FaFileWord, FaFilePowerpoint, FaFileImage } from "react-icons/fa";
import "./MediaKits.css";
import Footer from "../../components/Footer/Footer";
import FloatingWebsiteNavigation from "../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation";
import { useLanguage } from "../../contexts/LanguageContext";
import { generateCompanyOverviewPdf, generateExecutiveBiosPdf, generateCompanyFaqPdf, generateTermsPdf, generatePrivacyPdf } from "./utils/mediaKitPdfs";
import { DEFAULT_FAQ } from "../FAQ/FAQ";

// Import Press assets for High-Res Images download
import LogoCircular from "../../assets/svgs/logo circular1.png";
import Iwinto from "../../assets/svgs/iwinto.png";
import IwintoWhite from "../../assets/svgs/iwinto white.png";
import LogoCircularBlack from "../../assets/svgs/logo circular black.png";
import LogoCircularWhite from "../../assets/svgs/logo circular white.png";
import LogoTextBlack from "../../assets/svgs/logo text black.png";
import LogoTextWhite from "../../assets/svgs/logo text white.png";

// Media kit assets
const MEDIA_KITS = [
  { 
    name: "Company Overview", 
    url: "/assets/media-kits/company-overview.pdf", 
    type: "pdf",
    description: "Comprehensive overview of iWinto's mission, vision, and key statistics",
    size: "245 KB",
    icon: FaFilePdf,
    // Prefer generator if available for better formatting
    generator: async () => generateCompanyOverviewPdf(),
  },
  { 
    name: "Product Fact Sheet", 
    url: "/assets/media-kits/product-fact-sheet.pdf", 
    type: "pdf",
    description: "Detailed information about our products and services",
    size: "312 KB",
    icon: FaFilePdf
  },
  { 
    name: "Press Release Template", 
    url: "/assets/media-kits/press-release-template.docx", 
    type: "word",
    description: "Professional press release template for media partners",
    size: "28 KB",
    icon: FaFileWord
  },
  { 
    name: "Executive Bios", 
    url: "/assets/media-kits/executive-bios.pdf", 
    type: "pdf",
    description: "Biographies of key executives and leadership team",
    size: "189 KB",
    icon: FaFilePdf,
    generator: async () => generateExecutiveBiosPdf(),
  },
  { 
    name: "Company Presentation", 
    url: "/assets/media-kits/company-presentation.pptx", 
    type: "powerpoint",
    description: "Complete company presentation for media and investors",
    size: "45 KB",
    icon: FaFilePowerpoint
  },
  { 
    name: "High-Res Images", 
    url: "high-res-images", // Special identifier for press assets
    type: "archive",
    description: "Collection of high-resolution company images and logos from Press Center",
    size: "2.1 MB",
    icon: FaFileImage,
    isPressAssets: true // Flag to identify this as press assets download
  },
  { 
    name: "Company FAQ", 
    url: "/assets/media-kits/company-faq.pdf", 
    type: "pdf",
    description: "Frequently Asked Questions about iWinto platform, features, and policies",
    size: "1.8 MB",
    icon: FaFilePdf,
    // Will be bound at runtime with current-language content
    generator: null,
  },
  { 
    name: "Terms and Conditions", 
    url: "/assets/media-kits/terms-and-conditions.pdf", 
    type: "pdf",
    description: "Legal terms of service, user agreements, and platform policies",
    size: "2.4 MB",
    icon: FaFilePdf
  },
  { 
    name: "Privacy Policy", 
    url: "/assets/media-kits/privacy-policy.pdf", 
    type: "pdf",
    description: "Data protection policy, user privacy rights, and security measures",
    size: "2.9 MB",
    icon: FaFilePdf
  }
];

export default function MediaKits() {
  const { translations: t } = useLanguage();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [faqItemsForPdf, setFaqItemsForPdf] = useState([]);
  const [termsSections, setTermsSections] = useState([]);
  const [privacySections, setPrivacySections] = useState([]);

  // lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const mediaKits = useMemo(() => {
    return MEDIA_KITS.map((item) => {
      if (item.name === 'Company FAQ') {
        return { ...item, generator: async () => generateCompanyFaqPdf(faqItemsForPdf) };
      }
      if (item.name === 'Terms and Conditions') {
        return { ...item, generator: async () => generateTermsPdf(termsSections) };
      }
      if (item.name === 'Privacy Policy') {
        return { ...item, generator: async () => generatePrivacyPdf(privacySections) };
      }
      return item;
    });
  }, [faqItemsForPdf, termsSections, privacySections]);

  const categories = useMemo(() => {
    const cats = ["all"];
    mediaKits.forEach(kit => {
      if (!cats.includes(kit.type)) {
        cats.push(kit.type);
      }
    });
    return cats;
  }, [mediaKits]);

  const filteredKits = useMemo(() => {
    if (selectedCategory === "all") return mediaKits;
    return mediaKits.filter(kit => kit.type === selectedCategory);
  }, [mediaKits, selectedCategory]);

  // Bind FAQ/Terms/Privacy generator content lazily based on current language content
  useEffect(() => {
    try {
      // Always use DEFAULT_FAQ for comprehensive FAQ content
      // The translations might have limited questions, so we use the full DEFAULT_FAQ
      setFaqItemsForPdf(DEFAULT_FAQ);
      const termsSrc = t?.terms;
      const termsArr = [];
      if (termsSrc) {
        Object.keys(termsSrc).forEach((k) => {
          if (typeof termsSrc[k] === 'string') return;
          const sec = termsSrc[k];
          if (sec?.title || sec?.heading) {
            termsArr.push({ heading: sec.title || sec.heading, text: sec.text || sec.description || '' });
          }
        });
      }
      setTermsSections(termsArr);

      // Privacy
      const priv = t?.privacy;
      const pSections = [];
      const collect = (node) => {
        if (!node) return;
        Object.keys(node).forEach((k) => {
          const sec = node[k];
          if (sec && typeof sec === 'object') {
            if (sec.title && (sec.text || sec.description)) {
              pSections.push({ heading: sec.title, text: sec.text || sec.description });
            }
          }
        });
      };
      if (priv) {
        Object.keys(priv).forEach((k) => {
          if (k.startsWith('section')) collect(priv[k]);
        });
      }
      setPrivacySections(pSections);
    } catch (e) {
      // ignore
    }
  }, [t]);

  // Press assets for High-Res Images download
  const pressAssets = useMemo(() => [
    { name: "logo circular.png", url: LogoCircular },
    { name: "iwinto.png", url: Iwinto },
    { name: "iwinto white.png", url: IwintoWhite },
    { name: "logo circular black.png", url: LogoCircularBlack },
    { name: "logo circular white.png", url: LogoCircularWhite },
    { name: "logo text black.png", url: LogoTextBlack },
    { name: "logo text white.png", url: LogoTextWhite }
  ], []);

  const downloadOne = useCallback(async (url, filename, isPressAssets = false, generator = null) => {
    try {
      if (generator) {
        const blob = await generator();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
        return;
      }
      if (isPressAssets) {
        // Download all press assets as individual files
        for (const asset of pressAssets) {
          const res = await fetch(asset.url, { cache: "no-cache" });
          if (!res.ok) throw new Error(`Failed to fetch ${asset.name}`);
          const blob = await res.blob();
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = asset.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(objectUrl);
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } else {
        // Regular file download
        const res = await fetch(url, { cache: "no-cache" });
        if (!res.ok) throw new Error(`Failed to fetch ${filename}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename || "media-kit";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please try again later.");
    }
  }, [pressAssets]);

  const handleDownloadAll = useCallback(async () => {
    try {
      setDownloadingAll(true);
      for (const item of filteredKits) {
        await downloadOne(item.url, item.name, item.isPressAssets, item.generator);
        await new Promise((r) => setTimeout(r, 200));
      }
    } catch (e) {
      console.error(e);
      alert("Some files could not be downloaded. Check console for details.");
    } finally {
      setDownloadingAll(false);
    }
  }, [filteredKits, downloadOne]);

  // open/close preview
  const openPreview = useCallback((index) => {
    setCurrentIndex(index);
    setIsOpen(true);
  }, []);
  
  const closePreview = useCallback(() => {
    setIsOpen(false);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + filteredKits.length) % filteredKits.length);
  }, [filteredKits.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % filteredKits.length);
  }, [filteredKits.length]);

  // keyboard controls + scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, closePreview, goPrev, goNext]);

  const active = filteredKits[currentIndex];

  return (
    <>
      <div className="nav-desktop-only">
        <NavBar />
      </div>
      <div className="media-kit-page">

      <header className="media-kit-hero">
        <div className="media-kit-hero-inner">
          <h1 className="media-kit-title">{t?.mediaKits?.title || "Media Kits"}</h1>
          <p className="media-kit-subtitle">
            {t?.mediaKits?.subtitle || "Download comprehensive media resources including press releases, company information, and high-resolution assets."}
          </p>
        </div>
      </header>

      {/* Category Filter */}
      <div className="media-kit-filter">
        <div className="media-kit-filter-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`media-kit-filter-tab ${selectedCategory === category ? "active" : ""}`}
              onClick={() => setSelectedCategory(category)}
              type="button"
            >
              {category === "all" ? (t?.mediaKits?.filters?.all || "ALL").toUpperCase() : category.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="media-kit-actions">
          <button
            className="media-kit-download-all-btn"
            type="button"
            onClick={handleDownloadAll}
            disabled={downloadingAll || filteredKits.length === 0}
            aria-label={t?.mediaKits?.downloadAll || "Download all media kits"}
            title={t?.mediaKits?.downloadAll || "Download all media kits"}
          >
            <FaDownload aria-hidden="true" />
            <span>{downloadingAll ? (t?.mediaKits?.downloading || "DOWNLOADING…") : (t?.mediaKits?.downloadAll || "DOWNLOAD ALL")}</span>
          </button>
        </div>
      </div>

      {/* Media Kits Grid */}
      <section className="media-kit-grid" aria-label={t?.mediaKits?.gridLabel || "Media Kits Grid"}>
        {filteredKits.map((kit, i) => {
          const IconComponent = kit.icon;
          return (
            <article className="media-kit-card" key={`${kit.name}-${i}`}>
              <div
                className="media-kit-preview"
                role="button"
                tabIndex={0}
                onClick={() => openPreview(i)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPreview(i)}
                aria-label={`${t?.mediaKits?.openPreview || "Open preview"}: ${kit.name}`}
              >
                <div className="media-kit-file-icon">
                  <IconComponent />
                </div>
                <div className="media-kit-file-type-badge">{kit.type.toUpperCase()}</div>
                <button
                  type="button"
                  className="media-kit-download"
                  title={t?.mediaKits?.download || "Download"}
                  aria-label={`${t?.mediaKits?.download || "Download"} ${kit.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadOne(kit.url, kit.name, kit.isPressAssets, kit.generator);
                  }}
                >
                  <FaDownload />
                </button>
              </div>
              <div className="media-kit-meta">
                <h3 className="media-kit-name">{kit.name}</h3>
                <p className="media-kit-description">{kit.description}</p>
                <div className="media-kit-details">
                  <span className="media-kit-file-size">{kit.size}</span>
                  <span className="media-kit-file-type">{kit.type}</span>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* Lightbox */}
      {isOpen && active && (
        <div className="media-kit-lightbox" onClick={closePreview} role="dialog" aria-modal="true">
          <div
            className="media-kit-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="media-kit-lightbox-close" onClick={closePreview} aria-label={t?.mediaKits?.close || "Close"} title={t?.mediaKits?.close || "Close"}>
              <FaTimes />
            </button>

            <button className="media-kit-lightbox-nav left" onClick={goPrev} aria-label={t?.mediaKits?.previous || "Previous"}>
              <FaChevronLeft />
            </button>
            <button className="media-kit-lightbox-nav right" onClick={goNext} aria-label={t?.mediaKits?.next || "Next"}>
              <FaChevronRight />
            </button>

            <div className="media-kit-lightbox-content">
              <div className="media-kit-lightbox-file-info">
                <div className="media-kit-file-icon-large">
                  <active.icon />
                </div>
                <h3 className="media-kit-lightbox-name">{active.name}</h3>
                <p className="media-kit-lightbox-description">{active.description}</p>
                <div className="media-kit-lightbox-details">
                  <span className="media-kit-file-size">{active.size}</span>
                  <span className="media-kit-file-type">{active.type}</span>
                </div>
              </div>
            </div>

            <div className="media-kit-lightbox-footer">
              <button
                className="media-kit-lightbox-download"
                onClick={() => downloadOne(active.url, active.name, active.isPressAssets, active.generator)}
              >
                <FaDownload />
                <span>{t?.mediaKits?.download || "Download"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      </div>
      <FloatingWebsiteNavigation />
    </>
  );
}
